import React, { useState } from "react";
import { Phone, ZoomIn, X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { useI18n } from "../data/i18n";
import { restaurantInfo } from "../data/mock";
import { FEATURE_ICONS } from "../data/assets";
import chefsImg1 from "../assets/images/chefs/chefs.webp";
import chefsImg2 from "../assets/images/chefs/chefs1.webp";

const DiamondDivider = () => (
  <div className="flex items-center justify-center gap-2 my-4">
    <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#e4c590]"></div>
    <span className="diamond-mark"></span>
    <span className="diamond-mark opacity-60"></span>
    <span className="diamond-mark"></span>
    <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#e4c590]"></div>
  </div>
);

const OurChefs = () => {
  const { t, lang } = useI18n();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const chefImages = [chefsImg1, chefsImg2];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setCurrentImage(chefImages[index]);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImage(null);
  };

  const nextImage = (e) => {
    e?.stopPropagation();
    const nextIndex = (currentImageIndex + 1) % chefImages.length;
    setCurrentImageIndex(nextIndex);
    setCurrentImage(chefImages[nextIndex]);
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    const prevIndex = (currentImageIndex - 1 + chefImages.length) % chefImages.length;
    setCurrentImageIndex(prevIndex);
    setCurrentImage(chefImages[prevIndex]);
  };

  const heading1 = lang === "bg" ? "Виртуозите на" : "The Virtuosos of";
  const heading2 = lang === "bg" ? "Вкуса" : "Taste";
  const subtitle = lang === "bg" ? "Екипът зад кухнята ни" : "The team behind our kitchen";

  const body = lang === "bg"
    ? "В кухнята на CASA BALA всеки ден се ражда история от вкусове. Нашите готвачи превръщат най-свежите средиземноморски съставки в ястия, които разказват за своите корени."
    : "In the CASA BALA kitchen, a story of flavour is born every day. Our chefs turn the freshest Mediterranean ingredients into dishes that speak of their origins.";

  const body2 = lang === "bg"
    ? "Следваме сезоните и работим с избрани местни доставчици. С професионализъм и внимание към всеки детайл, ви каним в света на автентичните вкусове."
    : "We follow the seasons and work with carefully selected local suppliers. With professionalism and an eye for detail, we invite you into the world of authentic taste.";

  const features = [
    { title: t("features.fresh"), desc: t("features.fresh_desc") },
    { title: t("features.tastes"), desc: t("features.tastes_desc") },
    { title: t("features.chefs"), desc: t("features.chefs_desc") },
    { title: t("features.events"), desc: t("features.events_desc") },
  ];

  return (
    <div className="bg-[#0a0a0a] pt-32 pb-24 min-h-screen">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 mb-16 md:mb-24 text-center">
        <Reveal direction="up">
          <p className="section-label">{subtitle}</p>
          <h1 className="display text-4xl md:text-7xl lg:text-8xl text-[#f5f0e6] mt-3 leading-[1.2] md:leading-[1.05]">
            {heading1}<br /><span className="text-[#e4c590] italic">{heading2}</span>
          </h1>
          <DiamondDivider />
        </Reveal>
      </section>

      {/* Two-image with text - ЕДНАКЪВ СТИЛ ЗА ВСИЧКИ ЕКРАНИ */}
      <section className="max-w-7xl mx-auto px-6 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-24 items-start">
          
          {/* Лява колона - застъпени снимки (работи на всички екрани) */}
          <Reveal direction="left">
            <div className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[550px] lg:min-h-[600px]">
              {/* Голяма задна снимка */}
              <div className="absolute top-0 left-0 w-full h-full z-0">
                <div className="w-full h-full rounded-lg overflow-hidden cursor-pointer group shadow-2xl">
                  <div className="relative w-full h-full">
                    <img 
                      src={chefsImg1} 
                      alt="CASA BALA chefs background" 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div 
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center cursor-pointer"
                      onClick={() => openLightbox(0)}
                    >
                      <div className="bg-[#e4c590]/20 backdrop-blur-sm p-3 md:p-4 rounded-full">
                        <ZoomIn size={24} className="md:w-7 md:h-7 text-[#e4c590]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Малка предна снимка - позиционирана на всички екрани */}
              <div className="absolute -bottom-6 md:-bottom-8 -right-4 sm:-right-6 md:-right-8 lg:-right-12 w-[50%] sm:w-[55%] max-w-[200px] sm:max-w-[240px] md:max-w-[260px] lg:max-w-[280px] z-10 rounded-lg overflow-hidden cursor-pointer group shadow-2xl border-4 border-[#e4c590] bg-[#0a0a0a]">
                <div className="relative aspect-[4/5]">
                  <img 
                    src={chefsImg2} 
                    alt="CASA BALA chef foreground" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                      decoding="async"
                  />
                  <div 
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center cursor-pointer"
                    onClick={() => openLightbox(1)}
                  >
                    <div className="bg-[#e4c590]/20 backdrop-blur-sm p-2 md:p-3 rounded-full">
                      <ZoomIn size={18} className="md:w-5 md:h-5 text-[#e4c590]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Дясна колона - текстът */}
          <Reveal direction="right" delay={150}>
            <div className="lg:pl-4">
              <p className="section-label text-xs sm:text-sm">{t("about.team_label")}</p>
              <h2 className="display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#f5f0e6] mt-3 mb-4 md:mb-6">
                {t("about.team_title")}
              </h2>
              <DiamondDivider />
              <div className="space-y-3 md:space-y-4 text-[#cfc7b8] leading-relaxed text-sm sm:text-base">
                <p>{body}</p>
                <p>{body2}</p>
              </div>
              <div className="mt-6 md:mt-8">
                <a href={`tel:${restaurantInfo.phone}`} className="text-[#e4c590] flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity group">
                  <Phone size={16} className="md:w-5 md:h-5 group-hover:scale-110 transition-transform" />
                  <span className="text-base md:text-xl font-medium">{restaurantInfo.phone}</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-[#070707]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal direction="up">
            <div className="text-center mb-12 md:mb-16">
              <p className="section-label">{t("features.label")}</p>
              <h2 className="display text-3xl md:text-5xl text-[#f5f0e6] mt-3">{t("features.title")}</h2>
              <DiamondDivider />
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((f, idx) => (
              <Reveal key={idx} direction="up" delay={idx * 130}>
                <div className="text-center p-4 md:p-6 group">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-3 md:mb-6 flex items-center justify-center">
                    <img 
                      src={FEATURE_ICONS[idx]} 
                      alt={f.title} 
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1" 
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="display text-lg sm:text-xl md:text-2xl text-[#f5f0e6] mb-2 md:mb-3">{f.title}</h3>
                  <p className="text-[#a8a297] text-xs sm:text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && currentImage && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 cursor-pointer modal-overlay-animate bg-black/95"
        >
          <button 
            onClick={closeLightbox} 
            className="absolute top-4 right-4 text-white hover:text-[#e4c590] transition-colors z-10" 
            aria-label="Close"
          >
            <X size={24} />
          </button>
          
          <button 
            onClick={prevImage} 
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border border-[#e4c590]/40 text-[#e4c590] rounded-full flex items-center justify-center hover:bg-[#e4c590] hover:text-[#0a0a0a] transition-colors z-10" 
            aria-label="Previous"
          >
            <ChevronLeft size={16} className="sm:w-[18px] sm:h-[18px] md:w-[22px] md:h-[22px]" />
          </button>
          
          <img
            src={currentImage}
            alt="Chef gallery preview"
            className="max-w-full max-h-full object-contain modal-content-animate"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            onClick={nextImage} 
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 border border-[#e4c590]/40 text-[#e4c590] rounded-full flex items-center justify-center hover:bg-[#e4c590] hover:text-[#0a0a0a] transition-colors z-10" 
            aria-label="Next"
          >
            <ChevronRight size={16} className="sm:w-[18px] sm:h-[18px] md:w-[22px] md:h-[22px]" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[#cfc7b8] text-xs sm:text-sm">
            {currentImageIndex + 1} / {chefImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default OurChefs;